import { css } from '@linaria/core'
import { FrameTransport } from '@propps-au/frame-transport'
import { FrameMessage } from '@propps/frames-transport-messages'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import * as ScrollLock from 'scroll-lock'
import { createEffect, createSignal, onCleanup } from 'solid-js'
import { logger } from '../../util/logger'

export type EmbeddedFrameCommand =
  | NavigateCommand
  | SendCommand
  | ShowCommand
  | TriggerMountedCommand
  | SetSessionMetadataCommand

type NavigateCommand = {
  type: 'navigate'
  path: string
}

type SendCommand = {
  type: 'send'
  message: unknown
}

type ShowCommand = {
  type: 'show'
}

type SetSessionMetadataCommand = {
  type: 'set-session-metadata'
  customMetadata: string | null
  search: string
  referrer: {
    href: string
    path: string
    host: string
  }
}

type TriggerMountedCommand = {
  type: 'trigger-mounted'
  sessionId: string
  appId: string
  listingId: string
  foreignId: string
}

export function EmbeddedFrame(props: {
  appId: string
  origin: string
  commands: Observable<EmbeddedFrameCommand>
  onEvent?: (event: any) => void
}) {
  const [shouldLoadPage, setShouldLoadPage] = createSignal(false)
  const [transport, setTransport] = createSignal<FrameTransport>()
  let iframe!: HTMLIFrameElement

  createEffect(() => {
    const t = new FrameTransport(iframe.contentWindow!, props.origin)
    const message: FrameMessage.Configure = {
      type: 'configure',
      appId: props.appId,
    }
    t.send<FrameMessage.Configure>(message)
    logger.log('FrameMessage.Configure', message)

    setTransport(t)

    onCleanup(() => {
      t.destroy()
    })
  })

  // Forward messages into frame
  createSubscription(
    () =>
      props.commands.pipe(
        filter((command): command is SendCommand => command.type === 'send')
      ),
    (command) => {
      transport()?.send(command)
    }
  )

  // Handle frame events and forward to onEvent handler
  createSubscription(
    () => transport()?.messages(),
    (message) => {
      // Check if message is a frame event
      if (
        props.onEvent &&
        message &&
        typeof message === 'object' &&
        'type' in message &&
        typeof message.type === 'string' &&
        message.type.startsWith('frame-event:')
      ) {
        // Forward the original event to onEvent handler
        props.onEvent(message)
      }

      // Close the frame when the 'close' command is received
      if (
        message &&
        typeof message === 'object' &&
        'type' in message &&
        message.type === 'close'
      ) {
        setVisible(false)
      }
    }
  )

  // Forward the Navigate command
  createSubscription(
    () =>
      props.commands.pipe(
        filter(
          (command): command is NavigateCommand => command.type === 'navigate'
        )
      ),
    (command) => {
      const message: FrameMessage.Navigate = {
        type: 'navigate',
        path: command.path,
      }
      setShouldLoadPage(true)
      transport()?.send<FrameMessage.Navigate>(message)
      logger.log('FrameMessage.Navigate', message)
    }
  )

  // Forward trigger mounted event
  createSubscription(
    () =>
      props.commands.pipe(
        filter(
          (command): command is TriggerMountedCommand =>
            command.type === 'trigger-mounted'
        )
      ),
    (command) => {
      const message: FrameMessage.TriggerMounted = {
        type: 'trigger-mounted',
        sessionId: command.sessionId,
        appId: command.appId,
        listingId: command.listingId,
        foreignId: command.foreignId,
      }
      transport()?.send<FrameMessage.TriggerMounted>(message)
      logger.log('FrameMessage.TriggerMounted', message)
    }
  )

  // Forward session metadata
  createSubscription(
    () =>
      props.commands.pipe(
        filter(
          (command): command is SetSessionMetadataCommand =>
            command.type === 'set-session-metadata'
        )
      ),
    (command) => {
      const message: FrameMessage.SetSessionMetadata = {
        type: 'set-session-metadata',
        customMetadata: command.customMetadata,
        search: command.search,
        referrer: command.referrer,
      }
      transport()?.send<FrameMessage.SetSessionMetadata>(message)
      logger.log('FrameMessage.SetSessionMetadata', message)
    }
  )

  /** visibility state */
  const [visible, setVisible] = createSignal(false)
  const onClose = () => {
    setVisible(false)
  }

  // Show the frame when ShowCommand is received
  createSubscription(
    () =>
      props.commands.pipe(
        filter((command): command is ShowCommand => command.type === 'show')
      ),
    () => {
      setVisible(true)
    }
  )

  // Sync visibility state with frame
  createEffect(() => {
    const message = {
      type: 'set-visible',
      value: visible(),
    }
    transport()?.send(message)
    logger.log('FrameMessage.SetVisible', message)

    // Send frame opened/closed events
    if (props.onEvent && transport()) {
      if (visible()) {
        props.onEvent({
          type: 'frame-event:opened',
          timestamp: Date.now(),
        })
      } else {
        props.onEvent({
          type: 'frame-event:closed',
          timestamp: Date.now(),
        })
      }
    }
  })

  createEffect(() => {
    if (visible()) {
      ScrollLock.disablePageScroll()
      logger.log('ScrollLock', 'disabled page scroll.')
    } else {
      ScrollLock.enablePageScroll()
      logger.log('ScrollLock', 'enabled page scroll.')
    }
  })

  onCleanup(() => {
    ScrollLock.enablePageScroll()
    logger.log('ScrollLock', 'enabled page scroll.')
  })

  return (
    <>
      <div
        classList={{
          [styles.overlay]: true,
          [styles.overlayVisible]: visible(),
        }}
        onClick={onClose}
      />
      <button
        type="button"
        classList={{
          [styles.closeButton]: true,
          [styles.closeButtonVisible]: visible(),
        }}
        onClick={onClose}
        aria-label="Close Buyer Hub"
      >
        <CloseIcon />
      </button>
      <div
        classList={{
          [styles.modal]: true,
          [styles.modalVisible]: visible(),
        }}
      >
        <iframe
          class={styles.iframe}
          src={shouldLoadPage() ? props.origin : 'about:blank'}
          ref={iframe}
        />
      </div>
    </>
  )
}

function createSubscription<T>(
  source: () => Observable<T> | null | undefined,
  cb: (value: T) => void
) {
  createEffect(() => {
    const sub = source()?.subscribe(cb)
    onCleanup(() => {
      sub?.unsubscribe()
    })
  })
}

// This z-index is intentionally ridiculously high to ensure the Offer Form displays above any other content on the host page
export const zIndex = 999999999999999

const styles = {
  overlay: css`
    z-index: ${zIndex + 1};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    opacity: 0;
    visibility: hidden;
    background: rgba(0, 0, 0, 0.8);
    transition: 0.6s 0.3s opacity ease-in, 0.6s 0.3s visibility ease-in;
  `,
  overlayVisible: css`
    opacity: 1;
    visibility: visible;
    transition-delay: 0s;
    transition-duration: 0.9s;
    transition-timing-function: ease-out;
  `,
  modal: css`
    z-index: ${zIndex + 2};
    position: fixed;
    top: 64px;
    left: 50%;
    bottom: 0;
    width: 800px;
    max-width: 100%;
    overflow: hidden;
    overscroll-behavior-y: contain;
    background: #fff;
    box-shadow: 0 32px 64px 0 rgba(0, 0, 0, 0.2);
    border-radius: 24px 24px 0 0;
    opacity: 0;
    visibility: hidden;
    transform: translate(-50%, 288px);
    transition: 0.3s opacity ease-in, 0.3s visibility ease-in,
      0.3s transform ease-in;

    @media (min-height: 864px) {
      height: 800px;
      top: auto;
    }
  `,
  modalVisible: css`
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
    transition-timing-function: ease-out;
  `,
  iframe: css`
    height: 100%;
    max-height: none;
    width: 100%;
    max-width: none;
    border: none;
  `,
  closeButton: css`
    z-index: ${zIndex + 3};
    position: fixed;
    top: 12px;
    right: 12px;
    padding: 6px;
    background: none;
    border: none;
    opacity: 0;
    visibility: hidden;
    outline: none;
    cursor: pointer;
    transition: 0.3s opacity ease-in, 0.3s visibility ease-in;

    @media (min-width: 1024px) {
      & {
        top: 32px;
        right: 32px;
      }
    }

    svg {
      display: block;
      height: 32px;
      width: 32px;
    }
  `,
  closeButtonVisible: css`
    opacity: 0.7;
    visibility: visible;
    transition-timing-function: ease-out;

    &:hover,
    &:focus {
      opacity: 1;
    }
  `,
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden>
      <polygon
        id="Path"
        fill="#ffffff"
        points="16 14.5857864 23.0710678 7.51471863 24.4852814 8.92893219 17.4142136 16 24.4852814 23.0710678 23.0710678 24.4852814 16 17.4142136 8.92893219 24.4852814 7.51471863 23.0710678 14.5857864 16 7.51471863 8.92893219 8.92893219 7.51471863"
      ></polygon>
    </svg>
  )
}
